import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import appStyle from '../../style.less'
import style from './style.less'
import Typography, {
  TypographyTypes,
} from '@yaak/components/src/Typography/Typography'
import Divider from '@yaak/components/src/Divider'
import ProgressBar from '@yaak/components/src/ProgressBar'
import {
  getSimModuleCatalog,
  SimModuleCatalogEntry,
} from '@yaak/components/services/api/api'
import {
  ToastContext,
  ToastContextType,
} from '@yaak/components/context/toastContext'
import { formatGearModes, groupModules } from '../../helpers/moduleConfig'

interface CustomModulesPageProps {
  token: string
}

const CustomModulesPage: React.FunctionComponent<CustomModulesPageProps> = ({
  token,
}) => {
  const [loading, setLoading] = useState(true)
  const [modules, setModules] = useState<SimModuleCatalogEntry[]>([])
  const navigate = useNavigate()
  const { setShowToast } = useContext(ToastContext) as ToastContextType

  useEffect(() => {
    const fetchCatalog = async () => {
      const catalog = await getSimModuleCatalog({
        token,
        onAlert: setShowToast,
      })
      setModules((catalog?.modules || []).filter((module) => module.isCustom))
      setLoading(false)
    }

    token && fetchCatalog()
  }, [token])

  const grouped = useMemo(() => groupModules(modules), [modules])

  return (
    <div className={appStyle.page}>
      <Typography type={TypographyTypes.headline}>
        {'Custom Modules'}
      </Typography>
      <Divider />
      {loading ? (
        <ProgressBar />
      ) : modules.length === 0 ? (
        <div className={style.emptyState}>
          <Typography type={TypographyTypes.body}>
            {'No custom modules. Mark a module as custom on Sim Modules.'}
          </Typography>
        </div>
      ) : (
        <div className={style.modulesContainer}>
          <table className={style.moduleTable}>
            <thead>
              <tr>
                <th className={style.thIndex}>#</th>
                <th className={style.thName}>Module</th>
                <th className={style.thGear}>Gear modes</th>
                <th className={style.thVersions}>Supported versions</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((group) => (
                <React.Fragment key={group.groupName}>
                  <tr className={style.groupRow}>
                    <td colSpan={4} className={style.groupCell}>
                      <Typography type={TypographyTypes.title}>
                        {group.groupName}
                      </Typography>
                    </td>
                  </tr>
                  {group.modules.map((module) => (
                    <tr
                      key={module.moduleId}
                      className={style.moduleRow}
                      onClick={() =>
                        navigate(
                          `/simulator/custom-modules/${encodeURIComponent(module.moduleId)}`
                        )
                      }
                    >
                      <td className={style.tdIndex}>{module.displayIndex}</td>
                      <td className={style.tdName}>{module.title}</td>
                      <td className={style.tdGear}>
                        {formatGearModes(module.gearModes)}
                      </td>
                      <td className={style.tdVersions}>
                        {module.supportedVersions?.length
                          ? module.supportedVersions.join(', ')
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default CustomModulesPage
